import { useRef, useEffect } from "react";
import gsap from "gsap";
import { TYPE_ANIMATION } from "./CirclePanel";
import { createTallyLibrary } from "../../lib/TallyLibrary";
import {
  TallyAnimationProps,
  ParticleFunctionType,
} from "../../lib/TallyLibrary/types";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { mapRange } from "../../lib/TallyLibrary/helpers";
import AnimationManager from "../../lib/AnimationManager";

export default function CirclePanelAnimation({
  width,
  height,
  particleAmount,
  scale,
  blobSpeed,
  isResponsive,
  responsiveFactor,
  timelineRef,
}: TallyAnimationProps) {
  let particles: ParticleFunctionType[] = [];
  const settings = {
    width: width || SETTINGS.width,
    height: height || SETTINGS.height,
    blobSpeed: blobSpeed || SETTINGS.blobSpeed,
    particleAmount: particleAmount || SETTINGS.particleAmount,
    scale: scale || SETTINGS.scale,
    isResponsive: isResponsive || SETTINGS.isResponsive,
    responsiveFactor: responsiveFactor || SETTINGS.responsiveFactor,
  };
  const refCanvas = useRef<HTMLCanvasElement>(null);
  const particleLibrary = useRef<ReturnType<typeof createTallyLibrary> | null>(
    null,
  );
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const animationManager = AnimationManager.getInstance();
  const systemId = "inner-circle-animation";

  useEffect(() => {
    if (refCanvas.current && !particleLibrary.current) {
      // Cache canvas context to avoid repeated getContext calls
      canvasContextRef.current = refCanvas.current.getContext("2d");

      particleLibrary.current = createTallyLibrary({
        canvas: refCanvas.current,
        config: {
          dotColor: "brown",
          includeMagnitation: true,
          blobStrength: 0,
        },
      });

      const tl = gsap.timeline();
      gsap.delayedCall(TYPE_ANIMATION - 0.5, () => {
        tl.to(refCanvas.current, { autoAlpha: 1 });
        particleLibrary.current?.pulseAnimation();
      });
      if (timelineRef) {
        timelineRef.current = tl;
      }

      if (settings.isResponsive) {
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
      }
    }

    return () => {
      // Unregister from animation manager
      animationManager.unregisterParticleSystem(systemId);

      if (settings.isResponsive) {
        window.removeEventListener("resize", resizeHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      particleLibrary.current &&
      refCanvas.current &&
      canvasContextRef.current
    ) {
      setupParticlesWithManager();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [particleLibrary.current]);

  const setupParticlesWithManager = () => {
    if (
      particleLibrary.current &&
      refCanvas.current &&
      canvasContextRef.current
    ) {
      particles = [];
      for (let i = 0; i < settings.particleAmount; i++) {
        const particle = particleLibrary.current.createParticle("px", i);
        particles.push(particle);
      }

      const blobOffset = {
        x: 0,
        y: 0,
      };

      // Register with AnimationManager instead of creating local animation loop
      animationManager.registerParticleSystem({
        id: systemId,
        particles,
        blobOffset,
        settings,
        context: canvasContextRef.current,
        canvas: refCanvas.current,
        isActive: true, // Inner animation starts immediately
        mapRange,
      });
    }
  };

  const resizeHandler = () => {
    if (refCanvas.current) {
      const width = window.innerWidth * settings.responsiveFactor;
      const height = window.innerWidth * settings.responsiveFactor;
      refCanvas.current.style.width = width + "px";
      refCanvas.current.style.height = height + "px";
      particleLibrary.current?.update({ width, height });
    }
  };

  return (
    <canvas
      ref={refCanvas}
      className="tally-canvas"
      width={settings.width}
      height={settings.height}
    ></canvas>
  );
}
